import { useState } from 'react';

import { ChartCard, College, BarChartProps, SegmentedBarCard } from '@backrs_ui/common-ui';
import type { SegmentedBarRowData } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { LineIconItem } from '@backrs_ui/grow-ui/src/components/ResourceWrapper/LineIconItem';

import { Box, SxProps, Theme } from '@mui/material';

import { useAgentContext } from './WhoGetsIn.agentContext';
import {
  EXTRACURRICULAR_LEVELS,
  RIGOR_LEVELS,
  SELECTIVITY_LEVELS,
  getExtracurricularsLabel,
  getRigorLabel,
  getSelectivityLabel,
} from '../college.util';

const WHO_GETS_IN_STRINGS = appStrings.modules.college.whoGetsIn;

type WhoGetsInProps = Readonly<{
  college: College;
}>;

export const WhoGetsIn = ({ college }: WhoGetsInProps) => {
  useAgentContext(college);

  const [isSATOrACT, setIsSATOrACT] = useState<'SAT' | 'ACT'>(college.actcm25 ? 'ACT' : 'SAT');

  const SATData: BarChartProps = {
    chartMin: 400,
    chartMax: 1600,
    ticks: [400, 800, 1200, 1600],
    dataMin: (college.satmt25 ?? 0) + (college.satvr25 ?? 0),
    dataAverage: (college.satmt50 ?? 0) + (college.satvr50 ?? 0),
    dataMax: (college.satmt75 ?? 0) + (college.satvr75 ?? 0),
  };

  const ACTData: BarChartProps = {
    chartMin: 1,
    chartMax: 36,
    ticks: [1, 9, 18, 27, 36],
    dataMin: college.actcm25 ?? 0,
    dataAverage: college.actcm50 ?? 0,
    dataMax: college.actcm75 ?? 0,
  };

  const GPAData: BarChartProps = {
    chartMin: 0,
    chartMax: 4.5,
    ticks: [0, 1, 2, 3, 4],
    dataMin: college.gpa_25 ?? 0,
    dataAverage: college.gpa_50 ?? 0,
    dataMax: college.gpa_75 ?? 0,
  };

  const academicProfileRows: SegmentedBarRowData[] = [
    college.selectivity != null && {
      title: WHO_GETS_IN_STRINGS.selectivity.title,
      options: SELECTIVITY_LEVELS,
      selectedValue: getSelectivityLabel(college.selectivity),
      infoVariant: { type: 'modal' as const, modalData: WHO_GETS_IN_STRINGS.selectivity.modal },
    },
    college.extracurricular_involvement != null && {
      title: WHO_GETS_IN_STRINGS.charts.extracurriculars.title,
      options: EXTRACURRICULAR_LEVELS,
      selectedValue: getExtracurricularsLabel(college.extracurricular_involvement),
      infoVariant: {
        type: 'modal' as const,
        modalData: WHO_GETS_IN_STRINGS.charts.extracurriculars.modal,
      },
    },
    college.coursework_rigor != null && {
      title: WHO_GETS_IN_STRINGS.charts.rigor.title,
      options: RIGOR_LEVELS,
      selectedValue: getRigorLabel(college.coursework_rigor),
      infoVariant: { type: 'modal' as const, modalData: WHO_GETS_IN_STRINGS.charts.rigor.modal },
    },
  ].filter(Boolean) as SegmentedBarRowData[];

  return (
    <Box>
      {/* SEGMENT CHART + STAT LINE ITEMS */}
      <Box sx={topRowSx}>
        {academicProfileRows.length > 0 && (
          <SegmentedBarCard
            title={WHO_GETS_IN_STRINGS.segmentChartTitle}
            rows={academicProfileRows}
          />
        )}

        <Box sx={lineItemsContainerSx}>
          {college.admission_rate != null && (
            <LineIconItem
              title={WHO_GETS_IN_STRINGS.charts.acceptanceRate.title}
              iconName="PieChart02"
              text={`${Math.round(college.admission_rate * 100)}%`}
            />
          )}

          {college.early_decision_deadline && (
            <LineIconItem
              title={WHO_GETS_IN_STRINGS.earlyDecision}
              iconName="CalendarDate"
              text={`${new Date(college.early_decision_deadline).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}`}
            />
          )}

          {college.application_deadline && (
            <LineIconItem
              title={WHO_GETS_IN_STRINGS.applicationDeadline}
              iconName="CalendarDate"
              text={`${new Date(college.application_deadline).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}`}
            />
          )}
        </Box>
      </Box>

      {/* HIGH SCHOOL GPA AND SAT SCORE */}
      <Box sx={chartRowContainerSx}>
        <ChartCard
          title={WHO_GETS_IN_STRINGS.charts.highSchoolGPA.title}
          additionalInfoVariant={{
            type: 'TOOLTIP',
            toolTipText: WHO_GETS_IN_STRINGS.charts.highSchoolGPA.tooltip,
          }}
          chartVariant={{
            type: 'BAR',
            bar: GPAData,
          }}
          overrideSx={{ container: chartCardNoMaxWidthSx }}
        />

        {/* inlining the conditionals here gets us a nicer animation when the type changes */}
        <ChartCard
          title={WHO_GETS_IN_STRINGS.charts[isSATOrACT === 'SAT' ? 'satScore' : 'actScore'].title}
          additionalInfoVariant={{
            type: 'TOOLTIP',
            toolTipText:
              WHO_GETS_IN_STRINGS.charts[isSATOrACT === 'SAT' ? 'satScore' : 'actScore'].tooltip,
          }}
          headerAction={{
            label:
              WHO_GETS_IN_STRINGS.charts[isSATOrACT === 'SAT' ? 'satScore' : 'actScore'].switch,
            icon: 'RefreshCcw05',
            onClick: () => setIsSATOrACT((prev) => (prev === 'SAT' ? 'ACT' : 'SAT')),
          }}
          chartVariant={{
            type: 'BAR',
            bar: isSATOrACT === 'SAT' ? SATData : ACTData,
          }}
          overrideSx={{ container: chartCardNoMaxWidthSx }}
        />
      </Box>
    </Box>
  );
};

const topRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
  '@container (min-width: 700px)': {
    flexDirection: 'row',
  },
});

const lineItemsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
});

const chartRowContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: backrs.spacing['spacing-3xl'],
  gap: backrs.spacing['spacing-3xl'],
  flexWrap: 'wrap',
  '& > *': {
    flex: '1 1 100%',
    minWidth: 0,
  },
  '@container (min-width: 700px)': {
    '& > *': {
      flex: '1 1 0',
    },
  },
});

const chartCardNoMaxWidthSx: SxProps<Theme> = {
  maxWidth: 'none',
};
